import React, { useEffect, useRef, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { useQuery } from 'hooks/useQuery';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import { findInDictionary, mapDictionaryToOptionProp } from 'helpers/dictionary';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { Accordion } from 'components/molecules/Accordion';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Stack, Typography } from '@mui/material';
import { StyledDetailsWrapper } from 'pages/Firearms/RegisteredFirearmDeleteCommentPage/styles';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { Loader } from 'components/atoms/Loader';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { colors } from 'theme/colors';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EIconTypes } from 'constants/Icons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import Icon from 'components/atoms/Icon/Icon';
import Link from '@mui/material/Link';
import { useDictionary } from 'models/dictionaryContext';
import {
  EDictionaryTypes,
  ETransactionDictionaryItemTypes,
  IDictionary,
} from 'models/dictionary';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { EMIMETypes } from 'constants/MIMETypes';
import { createAncillaryInitialTransaction } from 'requests/transaction';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { AddAncillaryListItem } from 'components/organisms/AddAncillaryListItem';
import {
  convertSimpleDate,
  convertToSimpleDate,
  formatDateTimeToIso,
  formatOnlyDateToRequest,
} from 'utils/date';
import { useAddAncillaryFormSchema } from 'pages/LegalEntitiesPage/LegalEntityAddAncillaryPage/schemaValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  EArtAccessoryType,
  EArtAmmunitionType,
  EArtAncillaryTypeName,
  EArtComponentType,
} from 'constants/ArtAccessoryType';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { generateAncillariesListTemplate } from 'requests/ancillaries';
import {
  ECustomFieldObjectType,
  ECustomFieldType,
  ICustomFieldByObjectType,
} from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ICustomFieldForm } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useScroll } from 'hooks/useScroll';
import ImportAncillaries from 'components/organisms/ImportAncillaries';
import { getEnumByValue } from 'utils/enum';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { removeFileExtension } from 'utils/files';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getAttachmentError } from 'helpers/errors';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { FormModal } from 'components/molecules/Modal';
import {
  StyledFirearmListBox,
  StyledIconWrapper,
  StyledLine,
  StyledLocationMarkConfirmationMarginBottom,
  StyledTitleFirearmInformation,
} from './styles';
import { SelectLocationTransactionPage } from 'pages/Transactions/SelectLocationTransactionPage';
import { typesWithoutStrictAddressLocation } from '../../../utils/transactionUtils';

const MAX_FILE_SIZE_IN_MB = 3;

enum EMarkingType {
  IMPORT = 'IMPORT',
  APPLIED = 'APPLIED',
}

export const TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
  ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_DISASSEMBLED,
];

export const TRANSACTION_INITIAL_TYPES_FOR_AMMUNITION_EXPIRY_DATE_FIELD = [
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
  ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN,
];

export const TRANSACTION_INITIAL_TYPES_FOR_AMMUNITION_HEADSTAMP_YEAR_FIELD = [
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
  ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN,
];

export const TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD_REQUIRED = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
];

export const TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS = [
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
];

export const TRANSACTION_INITIAL_ILLEGAL = [
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
];

export const TRANSACTION_WITH_LOT_BATCH_NUMBER = [
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
  ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.SEIZED,
];

export const TRANSACTION_WITH_SERIAL_NUMBER = [
  ETransactionTypes.DECLARED_DISASSEMBLED,
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SURRENDERED,
];

export const TRANSACTION_WITH_IMPORTER = [
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_IMPORTED,
];

export const TRANSACTION_WITH_EXTERNAL_REFERENCE_NUMBER_MANDATORY = [
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
];

export const ACCESSORY_TYPES_WITHOUT_SERIAL_NUMBER = [
  EArtAccessoryType.MAGAZINES,
  EArtAccessoryType.LASER_LIGHT_MODULES,
  EArtAccessoryType.OPTICAL_SIGHTS,
];

export const ANCILLARY_TRANSACTIONS_WITH_AUTOCOMPLETED_MANUFACTURED_AT = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
];

export const ACCESSORY_TYPES_WITHOUT_MANUFACTURED_DATE = [
  EArtAccessoryType.OPTICAL_SIGHTS,
  EArtAccessoryType.LASER_LIGHT_MODULES,
  EArtAccessoryType.MAGAZINES,
];

const individualTransactionTypes = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
  ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_DISASSEMBLED,
];

const organizationTransactionTypes = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED,
  ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED,
  ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_DISASSEMBLED,
];

const LegalEntityAddAncillaryPage = () => {
  const { t } = useTranslations();

  const navigate = useNavigate();
  const { state: routerState }: any = useLocation();
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const { elRef, scrollToError } = useScroll();
  const { hasPermissions } = usePermission();
  const type: string | null = query.get('type')?.trim() || null;
  const initialFormValues: Record<string, any> = {
    typeUniqueId: routerState?.typeUniqueId || '',
    serialNumberEnabled: routerState?.serialNumberEnabled || true,
    importMarking: routerState?.importMarking || '',
    appliedMarking: routerState?.appliedMarking || '',
    lotBatchEnabled: routerState?.lotBatchEnabled || true,
    isAccessorySelected: routerState?.isAccessorySelected || false,
    exhibitNumberEnabled: routerState?.exhibitNumberEnabled || false,
    isLotBatchNumberNotPresent: routerState?.isLotBatchNumberNotPresent || false,
    validationOff: false,
    transactionDate: routerState?.transactionDate || '',
    firearmsQuantity: routerState?.firearmsQuantity || 'one',
    serialNumber: routerState?.serialNumber || '',
    serialNumberConfirmation: routerState?.serialNumberConfirmation || '',
    assetId: routerState?.assetId || '',
    exhibitNumber: routerState?.exhibitNumber || '',
    isExhibitNumberChecked: routerState?.isExhibitNumberChecked || false,
    lotNumber: routerState?.lotNumber || '',
    batchNumber: routerState?.batchNumber || '',
    manufacturedAt: routerState?.manufacturedAt || null,
    isCreateGroup: routerState?.isCreateGroup || 'no',
    groupName: routerState?.groupName || '',
    countryOfImportUniqueId: routerState?.countryOfImportUniqueId || '',
    importer: routerState?.importer || '',
    ancillaryList: routerState?.ancillaryList?.length
      ? routerState?.ancillaryList
      : [
          {
            serialNumber: '',
            serialNumberConfirmation: '',
            assetId: '',
            exhibitNumber: '',
            importMarking: '',
            appliedMarking: '',
            isExhibitNumberChecked: false,
            isLotBatchNumberNotPresent: false,
            ...(TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD?.includes(
              routerState?.typeUniqueId,
            )
              ? { manufacturedAt: null }
              : {}),
          },
        ],
    attachments: routerState?.attachments?.length ? routerState?.attachments : [],
    address: routerState?.address || '',
    unassignedLocation: routerState?.unassignedLocation || false,
    latitude: routerState?.coords ? routerState?.coords[0] : undefined,
    longitude: routerState?.coords ? routerState?.coords[1] : undefined,
    addressId: routerState?.addressId || null,
    countryUniqueId: routerState?.countryUniqueId || undefined,
    city: routerState?.city || undefined,
  };

  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>();
  const [isLegalEntityLoading, setIsLegalEntityLoading] = useState<boolean>(false);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>();
  const [isOrgLegalEntityLoading, setIsOrgLegalEntityLoading] = useState<boolean>(false);
  const [isErrorLocation, setIsErrorLocation] = useState<boolean>(false);
  const { loadDictionaries, getCustomField, customFieldsAncillaries } = useLegalEntities();
  const { isDesktop } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();

  const {
    getTransactionInitialTypes,
    transactionInitialTypes,
    getFirearmType,
    firearmType,
    make,
    getMake,
    model,
    getModel,
    manufacturer,
    getManufacturer,
    artComponentCalibre,
    getArtComponentCalibre,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getFirearmType();
      getManufacturer();
      getArtComponentCalibre();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const { setAlert } = useAlert();
  const [selectedAncillary, setSelectedAncillary] = useState<any>(null);
  const [createInitialTransactionError, setCreateInitialTransactionError] =
    useState<string>('');
  const [showImportDialog, setShowImportDialog] = useState<boolean>(false);
  const [isExhibitNumberChecked, setIsExhibitNumberChecked] = useState<boolean>(
    routerState?.isExhibitNumberChecked || false,
  );
  const [isLotBatchNumberNotPresent, setIsLotBatchNumberNotPresent] = useState<boolean>(
    routerState?.isLotBatchNumberNotPresent || false,
  );
  const { countryAll } = useDictionary();
  const { schema } = useAddAncillaryFormSchema(customFieldsAncillaries);
  const { shortDateFormat } = useGlobalProperty();
  const [serialNumberEnabled, setSerialNumberEnabled] = useState(
    routerState?.serialNumberEnabled || false,
  );
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const [isReloadCausedLocationSelect, setIsReloadCausedLocationSelect] = useState(
    routerState?.isReloadCausedLocationSelect || false,
  );
  const [isDataImportedFromFile, setIsDataImportedFromFile] = useState<boolean>(false);

  const [unassignedLocationModalOpened, setUnassignedLocationModalOpened] = useState(false);

  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    setError,
    getValues,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ancillaryList',
  });
  const attachments = useFieldArray({
    control,
    name: 'attachments',
  });

  const firearmsQuantityValue = watch('firearmsQuantity');
  const transactionTypeValue = watch('typeUniqueId');
  const isCreateGroupValue = watch('isCreateGroup');
  const transactionDate = watch('transactionDate');
  const unassignedLocation = watch('unassignedLocation');
  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const address = watch('address');
  const addressId = watch('addressId');

  const isShowList = firearmsQuantityValue === 'many';
  const isShowGroupNameFormElement = isCreateGroupValue === 'yes';
  const isShowDocument = true;
  const isAccessorySelected =
    selectedAncillary?.ancillary === EArtAncillaryTypeName.ACCESSORY ||
    routerState?.ancillaryType === EArtAncillaryTypeName.ACCESSORY;
  const isComponentSelected =
    selectedAncillary?.ancillary === EArtAncillaryTypeName.COMPONENTS ||
    routerState?.ancillaryType === EArtAncillaryTypeName.COMPONENTS;
  const isExhibitNumberEnabled =
    TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS.includes(transactionTypeValue);
  const isLotBatchEnabled =
    TRANSACTION_WITH_LOT_BATCH_NUMBER.includes(transactionTypeValue) &&
    selectedAncillary?.ancillary === EArtAncillaryTypeName.AMMUNITION;
  const isShowImporterDetails = TRANSACTION_WITH_IMPORTER.includes(transactionTypeValue);
  const isManufacturedDateEnabled =
    selectedAncillary?.ancillary === EArtAncillaryTypeName.ACCESSORY
      ? TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD.includes(transactionTypeValue) &&
        (transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED ||
          !ACCESSORY_TYPES_WITHOUT_MANUFACTURED_DATE.includes(
            selectedAncillary?.accessoryTypeUniqueId,
          ))
      : TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD.includes(transactionTypeValue);
  const isAmmunitionExpiryDateEnabled =
    TRANSACTION_INITIAL_TYPES_FOR_AMMUNITION_EXPIRY_DATE_FIELD.includes(transactionTypeValue);
  const isTransactionIllegal = TRANSACTION_INITIAL_ILLEGAL.includes(transactionTypeValue);
  const isAmmunitionHeadstampYearEnabled =
    TRANSACTION_INITIAL_TYPES_FOR_AMMUNITION_HEADSTAMP_YEAR_FIELD.includes(
      transactionTypeValue,
    );

  const isAppliedMarking =
    transactionTypeValue === ETransactionTypes.DECLARED_DISASSEMBLED ||
    transactionTypeValue === ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED ||
    transactionTypeValue === ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN ||
    transactionTypeValue === ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN ||
    transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED ||
    transactionTypeValue === ETransactionTypes.DECLARED_UNKNOWN_ORIGIN ||
    transactionTypeValue === ETransactionTypes.SEIZED ||
    transactionTypeValue === ETransactionTypes.RECOVERED ||
    transactionTypeValue === ETransactionTypes.SURRENDERED;
  const isImportMarking =
    transactionTypeValue === ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED ||
    transactionTypeValue === ETransactionTypes.DECLARED_IMPORTED;

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.CountryAll]);

      if (selectedAncillary) {
        if (selectedAncillary.ancillary) {
          getTransactionInitialTypes(
            selectedAncillary.ancillary.toUpperCase() as ETransactionDictionaryItemTypes,
          );
        } else if (routerState?.ancillaryType) {
          getTransactionInitialTypes(
            routerState.ancillaryType.toUpperCase() as ETransactionDictionaryItemTypes,
          );
        }

        const ancillaryType = selectedAncillary?.ancillary || routerState?.ancillaryType;
        if (ancillaryType === EArtAncillaryTypeName.ACCESSORY) {
          await getCustomField(ECustomFieldObjectType.AN_ACCESSORY);
        } else if (ancillaryType === EArtAncillaryTypeName.AMMUNITION) {
          await getCustomField(ECustomFieldObjectType.AN_AMMUNITION);
        } else if (ancillaryType === EArtAncillaryTypeName.COMPONENTS) {
          await getCustomField(ECustomFieldObjectType.AN_COMPONENT);
        }
      }
    })();
  }, [selectedLanguage, selectedAncillary]);

  useEffect(() => {
    if (routerState && routerState.isExhibitNumberChecked && !isExhibitNumberChecked) {
      setIsExhibitNumberChecked(true);
    }
  }, []);

  const clearUserInput = () => {
    setIsReloadCausedLocationSelect(false);

    setValue('lotNumber', '');
    setValue('batchNumber', '');
    setValue('assetId', '');
    setValue('serialNumber', '');
    setValue('serialNumberConfirmation', '');
    setValue('importMarking', '');
    setValue('appliedMarking', '');
    setValue('exhibitNumber', '');
    setValue('manufacturedAt', '');
    setValue('ammunitionExpiryDate', '');
    setValue('importer', '');
    setValue('countryOfImportUniqueId', '');
    clearErrors();

    customFieldsAncillaries.forEach((customField) => setValue(customField.name, ''));
  };

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(() => {
    if (transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED) {
      if (firearmsQuantityValue === 'one') {
        if (transactionDate instanceof Date) {
          const manufacturedAt: Date = new Date(transactionDate);
          manufacturedAt.setHours(0);
          manufacturedAt.setMinutes(0);
          manufacturedAt.setSeconds(0);
          manufacturedAt.setMilliseconds(0);
          setValue('manufacturedAt', manufacturedAt);
        } else {
          setValue('manufacturedAt', transactionDate);
        }
      } else if (firearmsQuantityValue === 'many') {
        if (transactionDate instanceof Date) {
          const manufacturedAt: Date = new Date(transactionDate);
          manufacturedAt.setHours(0);
          manufacturedAt.setMinutes(0);
          manufacturedAt.setSeconds(0);
          manufacturedAt.setMilliseconds(0);
          fields.forEach((_, index) => {
            setValue(`ancillaryList[${index}].manufacturedAt`, manufacturedAt);
          });
        } else {
          fields.forEach((_, index) => {
            setValue(`ancillaryList[${index}].manufacturedAt`, transactionDate);
          });
        }
      }
    }
  }, [transactionTypeValue, transactionDate, firearmsQuantityValue, setValue, fields]);

  useEffect(() => {
    if (selectedAncillary) {
      if (transactionTypeValue === '') {
        setSerialNumberEnabled(false);
        setValue('serialNumberEnabled', false);
        return;
      }

      const accessoryType: EArtAccessoryType = getEnumByValue(
        EArtAccessoryType,
        selectedAncillary.accessoryTypeUniqueId,
      );

      const isSerialDisabled =
        selectedAncillary.ancillary === EArtAncillaryTypeName.AMMUNITION ||
        (selectedAncillary.ancillary === EArtAncillaryTypeName.ACCESSORY &&
          selectedAncillary.accessoryType &&
          (!TRANSACTION_WITH_SERIAL_NUMBER.includes(transactionTypeValue) ||
            ACCESSORY_TYPES_WITHOUT_SERIAL_NUMBER.includes(accessoryType)));
      setValue('serialNumberEnabled', !isSerialDisabled);
      setSerialNumberEnabled(!isSerialDisabled);
    }
  }, [selectedAncillary, transactionTypeValue]);

  useEffect(() => {
    if (selectedAncillary) {
      setValue('isAccessorySelected', isAccessorySelected);
      setValue('exhibitNumberEnabled', isExhibitNumberEnabled);
      setValue('lotBatchEnabled', isLotBatchEnabled);
      if (!isReloadCausedLocationSelect) {
        setValue('isLotBatchNumberNotPresent', false);
        setValue('isExhibitNumberChecked', false);
        setIsLotBatchNumberNotPresent(false);
        setIsExhibitNumberChecked(false);
      }
    }
  }, [selectedAncillary, transactionTypeValue]);

  useEffect(() => {
    if (type === ELegalEntityTypes.individual) {
      (async function getLegalEntityDataInit() {
        setIsLegalEntityLoading(true);
        if (id) {
          const response = await getLegalEntity(id);
          if (response) {
            setLegalEntity(response);
          }
          setIsLegalEntityLoading(false);
        }
      })();
    } else if (type === ELegalEntityTypes.organisation) {
      (async function getLegalEntityDataInit() {
        setIsOrgLegalEntityLoading(true);
        if (id) {
          const response = await getOrgLegalEntity(id);
          if (response) {
            setOrgLegalEntity(response);
          }
          setIsOrgLegalEntityLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);

  useEffect(() => {
    if (routerState?.ancillary) {
      setSelectedAncillary({
        ancillary: routerState?.ancillaryType,
        ...routerState?.ancillary,
      });
    }
  }, [routerState]);

  useEffect(() => {
    if (
      ANCILLARY_TRANSACTIONS_WITH_AUTOCOMPLETED_MANUFACTURED_AT.includes(transactionTypeValue)
    ) {
      if (firearmsQuantityValue === 'one') {
        if (transactionDate instanceof Date) {
          const manufacturedAt: Date = new Date(transactionDate);
          manufacturedAt.setHours(0);
          manufacturedAt.setMinutes(0);
          manufacturedAt.setSeconds(0);
          manufacturedAt.setMilliseconds(0);
          setValue('manufacturedAt', manufacturedAt);
        } else {
          setValue('manufacturedAt', transactionDate);
        }
      } else if (firearmsQuantityValue === 'many') {
        if (transactionDate instanceof Date) {
          const manufacturedAt: Date = new Date(transactionDate);
          manufacturedAt.setHours(0);
          manufacturedAt.setMinutes(0);
          manufacturedAt.setSeconds(0);
          manufacturedAt.setMilliseconds(0);
          fields.forEach((_, index) => {
            setValue(`ancillaryList[${index}].manufacturedAt`, manufacturedAt);
          });
        }
        fields.forEach((_, index) => {
          setValue(`ancillaryList[${index}].manufacturedAt`, transactionDate);
        });
      }
    }
  }, [transactionTypeValue, transactionDate, firearmsQuantityValue, setValue, fields]);

  const quantityOptions = [
    {
      value: 'one',
      label: t('addAncillary.ancillaryDetails.section.one.radio'),
    },
    {
      value: 'many',
      label: t('addAncillary.ancillaryDetails.section.many.radio'),
    },
  ];

  const createGroupOptions = [
    {
      value: 'yes',
      label: t('general.yes.button'),
    },
    {
      value: 'no',
      label: t('general.no.button'),
    },
  ];

  const filteredTransactionInitialTypes = useRef<IDictionary[] | null>([]);
  const breadcrumbs = useRef([
    { label: t('addAncillary.legalEntities.breadcrumb'), route: ERouteLinks.LegalEntities },
    {
      label:
        type === ELegalEntityTypes.individual
          ? t('addAncillary.individual.breadcrumb')
          : t('addAncillary.organisation.breadcrumb'),
      route: `${ERouteLinks.LegalEntities}?expand=${
        type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
      }`,
    },
    {
      label: t('addAncillary.legalEntityDetails.breadcrumb'),
      route: `${ERouteLinks.ViewLegalEntity.replace(
        ':id',
        id,
      )}?type=${type}&expand=ancillaries`,
    },
    t('addAncillary.addAncillary.breadcrumb'),
  ]);

  breadcrumbs.current = [
    { label: t('addAncillary.legalEntities.breadcrumb'), route: ERouteLinks.LegalEntities },
    {
      label:
        type === ELegalEntityTypes.individual
          ? t('addAncillary.individual.breadcrumb')
          : t('addAncillary.organisation.breadcrumb'),
      route: `${ERouteLinks.LegalEntities}?expand=${
        type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
      }`,
    },
    {
      label: t('addAncillary.legalEntityDetails.breadcrumb'),
      route: `${ERouteLinks.ViewLegalEntity.replace(
        ':id',
        id,
      )}?type=${type}&expand=ancillaries`,
    },
    t('addAncillary.addAncillary.breadcrumb'),
  ];

  useEffect(() => {
    if (transactionInitialTypes) {
      if (type === ELegalEntityTypes.individual) {
        filteredTransactionInitialTypes.current = transactionInitialTypes?.filter(
          ({ uniqueId: id1 }) =>
            !TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS.some((id2) => id2 === id1),
        );
      } else if (type === ELegalEntityTypes.organisation) {
        filteredTransactionInitialTypes.current = transactionInitialTypes;
      }
    }
  }, [type, transactionInitialTypes]);

  useEffect(() => {
    customFieldsAncillaries.forEach((customField) => {
      if (routerState && routerState[customField.name]) {
        const value = routerState[customField.name] || '';
        setValue(customField.name, value);
      }
    });
  }, [customFieldsAncillaries]);

  const handleCreateInitialTransaction = () => {
    setCreateInitialTransactionError('');
    handleSubmit(async (data) => {
      const getCustomFieldsData = (ancillary: any) => {
        const customFieldsPayload: ICustomFieldForm[] = [];
        Object.keys(ancillary).map((key) => {
          const customField = customFieldsAncillaries.find((item) => item.name === key);
          if (customField && ancillary[key]) {
            const customFieldItem: ICustomFieldForm = {
              customFieldUniqueId: customField.uniqueId,
            };
            if (customField.customFieldType === ECustomFieldType.TEXT) {
              if (ancillary[key] === undefined || ancillary[key] === null) {
                customFieldItem.text = undefined;
              } else if (ancillary[key].trim() === '') {
                customFieldItem.text = undefined;
              } else {
                customFieldItem.text = ancillary[key];
              }
            } else if (customField.customFieldType === ECustomFieldType.BOOL) {
              customFieldItem.bool = ancillary[key] === '1';
            } else if (customField.customFieldType === ECustomFieldType.DATE) {
              customFieldItem.date = formatDateTimeToIso(ancillary[key]);
            }
            customFieldsPayload.push(customFieldItem);
            delete ancillary[key];
          }
          return false;
        });

        return customFieldsPayload?.length ? { customFields: customFieldsPayload } : {};
      };

      setIsErrorLocation(false);
      const date = formatDateTimeToIso(data.transactionDate);

      if (selectedAncillary === null) {
        return setCreateInitialTransactionError(t('addAncillary.youMustAddAncillary.text'));
      }

      if (!data.unassignedLocation && !(data.latitude && data.longitude) && !addressId) {
        return setIsErrorLocation(true);
      }

      const prepareAncillaryData = (item: any) => ({
        artUniqueId: selectedAncillary.id || selectedAncillary.uniqueId,
        artLocal: true,
        ...getCustomFieldsData({ ...item }),
        ...(item.importMarking
          ? {
              marking: {
                marking: item.importMarking,
                markingType: EMarkingType.IMPORT,
              },
            }
          : {}),
        ...(item.appliedMarking
          ? {
              marking: {
                marking: item.appliedMarking,
                markingType: EMarkingType.APPLIED,
              },
            }
          : {}),
        ...(item?.serialNumber
          ? { serialNumber: item.isExhibitNumberChecked ? {} : item.serialNumber }
          : {}),
        ...(item?.assetId ? { assetId: item.assetId } : {}),
        ...(item?.exhibitNumber ? { exhibitNumber: item.exhibitNumber } : {}),
        ...(item?.lotNumber ? { lotNumber: item.lotNumber } : {}),
        ...(item?.batchNumber ? { batchNumber: item.batchNumber } : {}),
        ...(item?.headstampYear ? { headstampYear: item.headstampYear } : {}),
        manufacturedAt:
          item.manufacturedAt && item.manufacturedAt !== ''
            ? formatOnlyDateToRequest(item.manufacturedAt)
            : null,
        ammunitionExpiryDate:
          item.ammunitionExpiryDate && item.ammunitionExpiryDate !== ''
            ? formatOnlyDateToRequest(item.ammunitionExpiryDate)
            : null,
      });
      const ancillaries = isShowList
        ? data.ancillaryList.map((ancillary: any) => prepareAncillaryData(ancillary))
        : [prepareAncillaryData(data)];

      const leId =
        type === ELegalEntityTypes.individual
          ? legalEntity?.uniqueId
          : orgLegalEntity?.uniqueId;

      try {
        setSubmitBlocked(true);
        await createAncillaryInitialTransaction({
          request: {
            ancillaryType: selectedAncillary?.ancillary
              ? selectedAncillary?.ancillary.toLocaleUpperCase()
              : routerState.ancillaryType.toUpperCase(),
            typeUniqueId: data.typeUniqueId,
            date,
            groupName: data.groupName,
            ownerId: leId,
            keeperId: leId,
            countryOfImportUniqueId: data.countryOfImportUniqueId,
            importer: data.importer,
            countryOfExportUniqueId: '',
            latitude: data.latitude,
            longitude: data.longitude,
            address: data.address,
            city: data.city,
            countryUniqueId: data.countryUniqueId,
            unassignedLocation: data.unassignedLocation,
            ancillaries,
          },
          ...(data.attachments?.filter((attachment: any) => attachment.file !== null)?.length
            ? { attachments: data.attachments }
            : {}),
        }).then(() => {
          if (isShowList && fields.length > 1) {
            setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
              text: t('addAncillaries.transactionCreatedSuccessfully.message'),
            });
          } else {
            setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
              text: t('addAncillary.transactionCreatedSuccessfully.message'),
            });
          }
          navigate(
            `${ERouteLinks.ViewLegalEntity.replace(
              ':id',
              id,
            )}?type=${type}&expand=ancillaries`,
          );
        });
      } catch (e: any) {
        setSubmitBlocked(false);
        setCreateInitialTransactionError(e.response.data?.message);
      }
      return true;
    })();
  };

  const removeFirearmButtonProps = {
    ...(isDesktop ? { icon: EIconTypes.delete } : { label: 'Delete' }),
    ...(isDesktop ? {} : { size: EButtonSizes.small }),
    ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
    ...(isDesktop
      ? { variant: EButtonVariants.outlinedIconTertiary }
      : { variant: EButtonVariants.outlined }),
    fullWidth: !isDesktop,
  };

  const validateFile = (file: File, index: number) => {
    const fileType: any = file.type;
    if (file) {
      if (file.size === 0) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.emptyFile.text'),
        });
        return false;
      }
      const fileSizeInMB = file.size / 1024 / 1024;
      if (fileSizeInMB > MAX_FILE_SIZE_IN_MB) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileSizeValidation.text'),
        });
        return false;
      }
      if (
        ![
          EMIMETypes.JPG,
          EMIMETypes.JPEG,
          EMIMETypes.PNG,
          EMIMETypes.PDF,
          EMIMETypes.XLSX,
        ].includes(fileType)
      ) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileTypeValidation.text'),
        });
        return false;
      }
    }
    clearErrors(`attachments[${index}].file`);
    return true;
  };

  const handleUploadFile = (file: File, index: number) => {
    if (validateFile(file, index)) {
      attachments.update(index, {
        file,
      });
    }
  };

  const renderFirearmDetailsActionButtons = (isTemplateFooter: boolean) => (
    <Grid
      container
      sx={{
        display: 'flex',
        justifyContent: isTemplateFooter ? 'flex-end' : 'space-between',
        alignItems: 'center',
        borderTop: {
          xs: `1px solid ${colors.primary25} `,
          lg: 'none',
        },
        pt: {
          xs: isTemplateFooter ? 0 : 2,
          lg: 0,
        },
        mt: {
          xs: isTemplateFooter ? 0 : 2,
          lg: 0,
        },
      }}
    >
      {!isDesktop && !isTemplateFooter && (
        <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {`${t('actions.general.label')}: `}
          </Typography>
        </Grid>
      )}
      <Grid
        container
        size={{ xs: 12, sm: 6 }}
        spacing={2}
        sx={{
          display: {
            xs: isTemplateFooter ? 'none' : 'flex',
            lg: isTemplateFooter ? 'flex' : 'none',
          },
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Grid size={{ xs: isTemplateFooter ? 'auto' : 12, sm: isTemplateFooter ? 'auto' : 6 }}>
          <Button
            {...removeFirearmButtonProps}
            onClick={() => {
              setSelectedAncillary(null);
              window.history.replaceState({}, document.title);
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  const renderRemoveAttachmentButton = (index: number) => (
    <Grid
      size={{ xs: 1 }}
      sx={!isDesktop ? { display: 'flex', justifyContent: 'flex-end' } : {}}
    >
      <Button
        variant={EButtonVariants.outlined}
        icon={EIconTypes.delete}
        onClick={() => attachments.remove(index)}
        sizePX={EButtonSizesPX.px32}
        sx={{ ...(isDesktop ? { marginLeft: '20px' } : {}), marginTop: '28px' }}
      />
    </Grid>
  );

  const importMapper = (
    transactionType: ETransactionTypes,
    ancillaryType: EArtAncillaryTypeName,
    artAccessoryTypeId: EArtAccessoryType,
    artAmmunitionTypeId: EArtAmmunitionType,
    artComponentTypeUniqueId: EArtComponentType,
    customFields: ICustomFieldByObjectType[],
  ) => {
    const getCustomFields = (index: number, customFieldItemRow: string[]) => {
      const res: any = {};
      customFields.map((field) => {
        if (field.customFieldType === ECustomFieldType.BOOL) {
          if (
            (customFieldItemRow &&
              customFieldItemRow[index] &&
              customFieldItemRow[index].toLowerCase().trim() === 'true') ||
            customFieldItemRow[index].toLowerCase().trim() === 'yes' ||
            customFieldItemRow[index].toLowerCase().trim() ===
              t('general.yes.label').toLowerCase()
          ) {
            res[field.name] = '1';
          } else if (
            (customFieldItemRow &&
              customFieldItemRow[index] &&
              customFieldItemRow[index].toLowerCase().trim() === 'false') ||
            customFieldItemRow[index].toLowerCase().trim() === 'no' ||
            customFieldItemRow[index].toLowerCase().trim() ===
              t('general.no.label').toLowerCase()
          ) {
            res[field.name] = '0';
          } else {
            res[field.name] = undefined;
          }
        } else if (field.customFieldType === ECustomFieldType.DATE) {
          res[field.name] =
            convertSimpleDate(customFieldItemRow?.[index], shortDateFormat) ?? '';
        } else {
          res[field.name] = customFieldItemRow?.[index] ?? '';
        }
        index++;
        return false;
      });
      return res;
    };

    if (ancillaryType === EArtAncillaryTypeName.ACCESSORY) {
      if (
        artAccessoryTypeId === EArtAccessoryType.MAGAZINES ||
        artAccessoryTypeId === EArtAccessoryType.LASER_LIGHT_MODULES ||
        artAccessoryTypeId === EArtAccessoryType.OPTICAL_SIGHTS
      ) {
        switch (transactionType) {
          case ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED:
          case ETransactionTypes.DECLARED_IMPORTED:
            return (itemRow: string[]) => ({
              importMarking: itemRow?.[0] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[1], shortDateFormat) ?? '',
              ...getCustomFields(2, itemRow),
            });
          case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
            return (itemRow: string[]) => ({
              appliedMarking: itemRow?.[0] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[1], shortDateFormat) ?? '',
              ...getCustomFields(2, itemRow),
            });
          case ETransactionTypes.DECLARED_MANUFACTURED:
            return (itemRow: string[]) => ({
              appliedMarking: itemRow?.[0] ?? '',
              ...getCustomFields(1, itemRow),
            });
          case ETransactionTypes.RECOVERED:
          case ETransactionTypes.SEIZED:
          case ETransactionTypes.SURRENDERED:
            return (itemRow: string[]) => ({
              exhibitNumber: itemRow?.[0] ?? '',
              appliedMarking: itemRow?.[1] ?? '',
              ...getCustomFields(2, itemRow),
            });
          default:
            return null;
        }
      }
      if (
        artAccessoryTypeId === EArtAccessoryType.FLASH_ELIMINATORS ||
        artAccessoryTypeId === EArtAccessoryType.SUPPRESSORS
      ) {
        switch (transactionType) {
          case ETransactionTypes.DECLARED_IMPORTED:
          case ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              importMarking: itemRow?.[1] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[2], shortDateFormat) ?? '',
              ...getCustomFields(3, itemRow),
            });
          case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              appliedMarking: itemRow?.[1] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[2], shortDateFormat) ?? '',
              ...getCustomFields(3, itemRow),
            });
          case ETransactionTypes.DECLARED_MANUFACTURED:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              appliedMarking: itemRow?.[1] ?? '',
              ...getCustomFields(2, itemRow),
            });
          case ETransactionTypes.RECOVERED:
          case ETransactionTypes.SEIZED:
          case ETransactionTypes.SURRENDERED:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              exhibitNumber: itemRow?.[1] ?? '',
              appliedMarking: itemRow?.[2] ?? '',
              ...getCustomFields(3, itemRow),
            });
          default:
            return null;
        }
      }
    }
    if (ancillaryType === EArtAncillaryTypeName.AMMUNITION) {
      if (
        artAmmunitionTypeId === EArtAmmunitionType.GRENADE_LAUNCHER ||
        artAmmunitionTypeId === EArtAmmunitionType.HANDGUN_RIFLE ||
        artAmmunitionTypeId === EArtAmmunitionType.MISSILES ||
        artAmmunitionTypeId === EArtAmmunitionType.MORTAR_SHELL ||
        artAmmunitionTypeId === EArtAmmunitionType.RECOILLESS_ARTILLERY_SHELL ||
        artAmmunitionTypeId === EArtAmmunitionType.ROCKET_PROPELLED ||
        artAmmunitionTypeId === EArtAmmunitionType.SHOTGUN
      ) {
        switch (transactionType) {
          case ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED:
          case ETransactionTypes.DECLARED_IMPORTED:
            return (itemRow: string[]) => ({
              lotNumber: itemRow?.[0] ?? '',
              batchNumber: itemRow?.[1] ?? '',
              importMarking: itemRow?.[2] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[3], shortDateFormat) ?? '',
              ammunitionExpiryDate: convertSimpleDate(itemRow?.[4], shortDateFormat) ?? '',
              ...getCustomFields(5, itemRow),
            });
          case ETransactionTypes.DECLARED_LOT_BATCH_UNKNOWN_ORIGIN:
            return (itemRow: string[]) => ({
              lotNumber: itemRow?.[0] ?? '',
              batchNumber: itemRow?.[1] ?? '',
              appliedMarking: itemRow?.[2] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[3], shortDateFormat) ?? '',
              ammunitionExpiryDate: convertSimpleDate(itemRow?.[4], shortDateFormat) ?? '',
              ...getCustomFields(5, itemRow),
            });
          case ETransactionTypes.DECLARED_LOT_BATCH_MANUFACTURED:
            return (itemRow: string[]) => ({
              lotNumber: itemRow?.[0] ?? '',
              batchNumber: itemRow?.[1] ?? '',
              appliedMarking: itemRow?.[2] ?? '',
              ammunitionExpiryDate: convertSimpleDate(itemRow?.[3], shortDateFormat) ?? '',
              ...getCustomFields(4, itemRow),
            });
          case ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN:
            return (itemRow: string[]) => ({
              lotNumber: itemRow?.[0] ?? '',
              batchNumber: itemRow?.[1] ?? '',
              assetId: itemRow?.[2] ?? '',
              appliedMarking: itemRow?.[3] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[4], shortDateFormat) ?? '',
              ammunitionExpiryDate: convertSimpleDate(itemRow?.[5], shortDateFormat) ?? '',
              ...getCustomFields(6, itemRow),
            });
          case ETransactionTypes.RECOVERED:
          case ETransactionTypes.SEIZED:
          case ETransactionTypes.SURRENDERED:
            return (itemRow: string[]) => ({
              lotNumber: itemRow?.[0] ?? '',
              batchNumber: itemRow?.[1] ?? '',
              exhibitNumber: itemRow?.[2] ?? '',
              appliedMarking: itemRow?.[3] ?? '',
              ...getCustomFields(4, itemRow),
            });
          default:
            return null;
        }
      }
    }
    if (ancillaryType === EArtAncillaryTypeName.COMPONENTS) {
      if (
        artComponentTypeUniqueId === EArtComponentType.BARREL ||
        artComponentTypeUniqueId === EArtComponentType.BOLT ||
        artComponentTypeUniqueId === EArtComponentType.FRAME ||
        artComponentTypeUniqueId === EArtComponentType.LOWER_RECEIVER ||
        artComponentTypeUniqueId === EArtComponentType.UPPER_RECEIVER ||
        artComponentTypeUniqueId === EArtComponentType.SLIDE ||
        artComponentTypeUniqueId === EArtComponentType.CYLINDER
      ) {
        switch (transactionType) {
          case ETransactionTypes.DECLARED_LOT_BATCH_IMPORTED:
          case ETransactionTypes.DECLARED_IMPORTED:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              importMarking: itemRow?.[1] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[2], shortDateFormat) ?? '',
              ...getCustomFields(3, itemRow),
            });
          case ETransactionTypes.DECLARED_DISASSEMBLED:
          case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              appliedMarking: itemRow?.[1] ?? '',
              manufacturedAt: convertSimpleDate(itemRow?.[2], shortDateFormat) ?? '',
              ...getCustomFields(3, itemRow),
            });
          case ETransactionTypes.DECLARED_MANUFACTURED:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              appliedMarking: itemRow?.[1] ?? '',
              ...getCustomFields(2, itemRow),
            });
          case ETransactionTypes.RECOVERED:
          case ETransactionTypes.SEIZED:
          case ETransactionTypes.SURRENDERED:
            return (itemRow: string[]) => ({
              serialNumber: itemRow?.[0] ?? '',
              serialNumberConfirmation: itemRow?.[0] ?? '',
              exhibitNumber: itemRow?.[1] ?? '',
              appliedMarking: itemRow?.[2] ?? '',
              ...getCustomFields(3, itemRow),
            });
          default:
            return null;
        }
      }
    }
    return null;
  };

  const mapAvailableTransactionType = (dictionaries: IDictionary[] | null) => {
    if (dictionaries) {
      const transactionTypes = dictionaries.filter((item) => {
        if (item) {
          return type === ELegalEntityTypes.individual
            ? individualTransactionTypes.includes(
                item.uniqueId.toString() as ETransactionTypes,
              )
            : organizationTransactionTypes.includes(
                item.uniqueId.toString() as ETransactionTypes,
              );
        }
        return false;
      });

      transactionTypes.sort((a, b) => a.name.localeCompare(b.name));

      return mapDictionaryToOptionProp(transactionTypes);
    }
    return [];
  };

  const handleExhibitNumberChange = () => {
    setIsExhibitNumberChecked((prevState) => !prevState);
    setValue('serialNumber', '');
    setValue('serialNumberConfirmation', '');
  };

  const handleIsLotBatchNumberPresentChange = () => {
    setIsLotBatchNumberNotPresent((prevState) => !prevState);
    setValue('lotNumber', '');
    setValue('batchNumber', '');
  };

  const handleGenerateAncillariesListTemplate = async () => {
    const data = getValues();
    const getCustomFieldsData = (ancillary: any) => {
      const customFieldsPayload: ICustomFieldForm[] = [];
      Object.keys(ancillary).map((key) => {
        const customField = customFieldsAncillaries.find((item) => item.name === key);
        if (customField && ancillary[key]) {
          const customFieldItem: ICustomFieldForm = {
            customFieldUniqueId: customField.uniqueId,
          };
          if (customField.customFieldType === ECustomFieldType.TEXT) {
            if (ancillary[key] === undefined || ancillary[key] === null) {
              customFieldItem.text = undefined;
            } else if (ancillary[key].trim() === '') {
              customFieldItem.text = undefined;
            } else {
              customFieldItem.text = ancillary[key];
            }
          } else if (customField.customFieldType === ECustomFieldType.BOOL) {
            customFieldItem.bool =
              ancillary[key] === '1' ? t('general.yes.label') : t('general.no.label');
          } else if (customField.customFieldType === ECustomFieldType.DATE) {
            customFieldItem.date = convertToSimpleDate(ancillary[key], shortDateFormat);
          }
          customFieldsPayload.push(customFieldItem);
        }
        return false;
      });
      return customFieldsPayload?.length ? { customFields: customFieldsPayload } : {};
    };

    if (transactionTypeValue === '') return false;
    const body = {
      typeUniqueId: data.typeUniqueId,
      ancillaryType: selectedAncillary?.ancillary
        ? selectedAncillary?.ancillary.toLocaleUpperCase()
        : routerState.ancillaryType.toUpperCase(),
      accessoryTypeUniqueId: selectedAncillary?.accessoryTypeUniqueId,
      values: data.ancillaryList.map((item: any) => ({
        ...getCustomFieldsData(item),
        serialNumber: !item.isExhibitNumberChecked ? item.serialNumber : null,
        assetId: item.assetId || null,
        lotNumber: item.lotNumber || null,
        batchNumber: item.batchNumber || null,
        exhibitNumber: item.exhibitNumber || null,
        ...(isManufacturedDateEnabled && item.manufacturedAt && item.manufacturedAt !== ''
          ? {
              manufacturedAt: item.manufacturedAt
                ? convertToSimpleDate(item.manufacturedAt, shortDateFormat)
                : null,
            }
          : {}),
        ...(isAmmunitionExpiryDateEnabled &&
        item.ammunitionExpiryDate &&
        item.ammunitionExpiryDate !== ''
          ? {
              ammunitionExpiryDate: item.ammunitionExpiryDate
                ? convertToSimpleDate(item.ammunitionExpiryDate, shortDateFormat)
                : null,
            }
          : {}),
        ...(item.importMarking
          ? {
              marking: {
                marking: item.importMarking,
                markingType: EMarkingType.IMPORT,
              },
            }
          : {}),
        ...(item.appliedMarking
          ? {
              marking: {
                marking: item.appliedMarking,
                markingType: EMarkingType.APPLIED,
              },
            }
          : {}),
      })),
    };

    try {
      await generateAncillariesListTemplate(body);
    } catch (e: any) {
      setSubmitBlocked(false);
      console.error(e);
    }
    return true;
  };

  const importItem = (itemRow: string[]) => {
    const mapper = importMapper(
      transactionTypeValue,
      selectedAncillary?.ancillary,
      selectedAncillary?.accessoryTypeUniqueId,
      selectedAncillary?.ammunitionTypeUniqueId,
      selectedAncillary?.componentTypeUniqueId,
      customFieldsAncillaries,
    );

    return mapper ? append(mapper(itemRow)) : null;
  };

  const attachmentNames = watch('attachments');

  useEffect(() => {
    attachments.fields.forEach((attachment: any, index) => {
      if (attachment?.file?.name && !attachmentNames[index]?.attachmentName) {
        setValue(
          `attachments.${index}.attachmentName`,
          removeFileExtension(attachment.file.name),
        );
      }
    });
  }, [attachments.fields, attachmentNames]);

  const canSelectFromMap = typesWithoutStrictAddressLocation.includes(
    transactionTypeValue as ETransactionTypes,
  );

  return (
    <div ref={elRef}>
      <Breadcrumbs items={breadcrumbs.current} />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">{t('addAncillary.addAncillary.header')}</StyledPageTitle>
      </Box>

      {!selectedAncillary && (
        <Box sx={{ mb: 2 }}>
          <Alert
            text={t('addAncillary.pleaseSelectAncillary.text')}
            variant={EAlertVariants.warning}
          />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 3 }}>
          <FormSelect
            options={mapAvailableTransactionType(transactionInitialTypes)}
            name="typeUniqueId"
            setValue={setValue}
            label={`${t('addAncillary.transactionType.label')}* `}
            onSelect={clearUserInput}
            control={control}
            errors={errors}
            disabled={!selectedAncillary}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <FormDateTimePicker
            name="transactionDate"
            label={`${t('addFirearm.transactionDate.label')}* `}
            control={control}
            errors={errors}
            currentHour={true}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Accordion title={t('addAncillary.ownerAndKeeper.section.name')} alwaysExpanded>
            <Box sx={{ mb: 5 }}>
              {type === ELegalEntityTypes.individual && (
                <div>
                  {isLegalEntityLoading ? (
                    <Loader isLoading={isLegalEntityLoading} />
                  ) : (
                    legalEntity && (
                      <StyledDetailsWrapper container sx={{ rowGap: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addAncillary.ownerAndKeeper.section.owner.label')}
                          </Typography>
                          <StyledLegalLink
                            legalId={legalEntity.uniqueId}
                            legalType={ELegalEntityTypes.individual}
                            labelText={`${legalEntity?.firstName} ${legalEntity?.lastName} `}
                            legalVisibilityUniqueId={legalEntity.visibilityUniqueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addAncillary.ownerAndKeeper.section.keeper.label')}
                          </Typography>
                          <StyledLegalLink
                            legalId={legalEntity.uniqueId}
                            legalType={ELegalEntityTypes.individual}
                            labelText={`${legalEntity?.firstName} ${legalEntity?.lastName} `}
                            legalVisibilityUniqueId={legalEntity.visibilityUniqueId}
                          />
                        </Grid>
                      </StyledDetailsWrapper>
                    )
                  )}
                </div>
              )}
              {type === ELegalEntityTypes.organisation && (
                <div>
                  {isOrgLegalEntityLoading ? (
                    <Loader isLoading={isOrgLegalEntityLoading} />
                  ) : (
                    orgLegalEntity && (
                      <StyledDetailsWrapper container sx={{ rowGap: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addAncillary.ownerAndKeeper.section.owner.label')}
                          </Typography>
                          <StyledLegalLink
                            legalId={orgLegalEntity.uniqueId}
                            legalType={ELegalEntityTypes.organisation}
                            labelText={orgLegalEntity?.organizationName}
                            legalVisibilityUniqueId={orgLegalEntity.visibilityUniqueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addAncillary.ownerAndKeeper.section.keeper.label')}
                          </Typography>
                          <StyledLegalLink
                            legalId={orgLegalEntity.uniqueId}
                            legalType={ELegalEntityTypes.organisation}
                            labelText={orgLegalEntity?.organizationName}
                            legalVisibilityUniqueId={orgLegalEntity.visibilityUniqueId}
                          />
                        </Grid>
                      </StyledDetailsWrapper>
                    )
                  )}
                </div>
              )}
            </Box>
          </Accordion>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Accordion title={t('addAncillary.ancillaryDetails.section.name')} alwaysExpanded>
            <Grid container size={{ xs: 12, lg: 3 }} sx={{ mb: 2 }}>
              <Button
                id="add_ancillary_select-button"
                fullWidth
                label={t('addAncillary.selectAncillary.button')}
                size={EButtonSizes.small}
                variant={EButtonVariants.outlined}
                onClick={() => {
                  clearUserInput();
                  navigate(
                    `${ERouteLinks.SearchForARTAncillary.replace(
                      ':id',
                      id as string,
                    )}?type=${type}&expand=ancillaries`,
                    { state: { ...routerState, ...getValues() } },
                  );
                }}
              />
            </Grid>
            {selectedAncillary && (
              <>
                <Grid
                  container
                  sx={{
                    border: `1px solid ${colors.primary25} `,
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      backgroundColor: colors.primary100,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {selectedAncillary.type ||
                          selectedAncillary.componentType ||
                          selectedAncillary.ammunitionType ||
                          selectedAncillary.accessoryType}
                      </Typography>
                    </Grid>
                    {renderFirearmDetailsActionButtons(true)}
                  </Grid>
                  <Grid container rowSpacing={2} size={{ xs: 12 }} sx={{ p: 2 }}>
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addAncillary.details.section.ancillary.label')}
                        </Typography>
                        <Typography variant="body2">
                          {selectedAncillary?.ancillary || routerState?.ancillaryType}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addAncillary.details.section.type.label')}
                        </Typography>
                        <Typography variant="body2">
                          {selectedAncillary?.type ||
                            selectedAncillary?.componentType ||
                            selectedAncillary?.accessoryType ||
                            selectedAncillary?.ammunitionType}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addAncillary.details.section.artId.label')}
                        </Typography>
                        <Typography variant="body2">{selectedAncillary?.artId}</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 3 }}>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <Link
                        href={`${ERouteLinks.ViewAncillaries.replace(
                          ':type',
                          selectedAncillary?.ancillary?.toLowerCase(),
                        ).replace(':id', selectedAncillary?.id)} `}
                        underline="none"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          fullWidth
                          label={t('addAncillary.details.section.showARTInformation.button')}
                          variant={EButtonVariants.contained}
                          size={EButtonSizes.small}
                        />
                      </Link>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {(() => {
                            if (selectedAncillary?.componentType) {
                              return t(
                                'addAncillary.details.section.componentManufacturer.label',
                              );
                            }
                            if (selectedAncillary?.accessoryType) {
                              return t(
                                'addAncillary.details.section.accessoryManufacturer.label',
                              );
                            }
                            if (selectedAncillary?.ammunitionType) {
                              return t(
                                'addAncillary.details.section.ammunitionManufacturer.label',
                              );
                            }
                            return '';
                          })()}
                        </Typography>
                        <Typography variant="body2">
                          {selectedAncillary?.manufacturer}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addAncillary.details.section.calibre.label')}
                        </Typography>
                        <Typography variant="body2">
                          {findInDictionary(
                            selectedAncillary?.calibreUniqueId,
                            artComponentCalibre,
                          )}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addAncillary.details.section.composition.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(selectedAncillary?.composition)}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addAncillary.details.section.proofHouse.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(selectedAncillary?.proofHouse)}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                  {(selectedAncillary.ancillary === EArtAncillaryTypeName.COMPONENTS ||
                    routerState?.ancillaryType === EArtAncillaryTypeName.COMPONENTS) && (
                    <>
                      <StyledLine size={{ xs: 12 }} />
                      <Grid container rowSpacing={2} size={{ xs: 12 }} sx={{ p: 2 }}>
                        <Grid size={{ xs: 12 }}>
                          <Stack>
                            <StyledTitleFirearmInformation variant="subtitle1">
                              {t('addAncillary.details.section.firearmInformation.label')}
                            </StyledTitleFirearmInformation>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addAncillary.details.section.firearmType.label')}
                            </Typography>
                            <Typography variant="body2">
                              {findInDictionary(
                                selectedAncillary?.frtTypeUniqueId,
                                firearmType,
                              )}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addAncillary.details.section.firearmManufacturer.label')}
                            </Typography>
                            <Typography variant="body2">
                              {findInDictionary(
                                selectedAncillary?.frtManufacturerUniqueId,
                                manufacturer,
                              )}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addAncillary.details.section.make.label')}
                            </Typography>
                            <Typography variant="body2">
                              {findInDictionary(selectedAncillary?.frtMakeUniqueId, make)}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addAncillary.details.section.model.label')}
                            </Typography>
                            <Typography variant="body2">
                              {findInDictionary(selectedAncillary?.frtModelUniqueId, model)}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <FormRadioGroup
                      name="firearmsQuantity"
                      label={t('addAncillary.details.section.howMany.text')}
                      options={quantityOptions}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  {firearmsQuantityValue === 'one' ? (
                    <Grid size={{ xs: 12 }} container columnSpacing={2}>
                      {isLotBatchEnabled && (
                        <>
                          {isTransactionIllegal && (
                            <Grid
                              size={{ xs: 12, lg: 3 }}
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <FormCheckbox
                                name="isLotBatchNumberNotPresent"
                                label={t('addAncillary.validation.lotBatchNotPresent')}
                                control={control}
                                errors={errors}
                                onChange={handleIsLotBatchNumberPresentChange}
                              />
                            </Grid>
                          )}
                          <Grid size={{ xs: 12, lg: 3 }}>
                            <FormInput
                              name="lotNumber"
                              label={`${t('addAncillary.list.section.lotNumber.label')} `}
                              setValue={setValue}
                              control={control}
                              charsLimit={10}
                              errors={errors}
                              disabled={isLotBatchNumberNotPresent}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, lg: 3 }}>
                            <FormInput
                              name="batchNumber"
                              label={`${t('addAncillary.list.section.batchNumber.label')} `}
                              setValue={setValue}
                              control={control}
                              charsLimit={10}
                              errors={errors}
                              disabled={isLotBatchNumberNotPresent}
                            />
                          </Grid>
                        </>
                      )}
                      {isExhibitNumberEnabled && serialNumberEnabled && (
                        <Grid
                          size={{ xs: 12, lg: 3 }}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <FormCheckbox
                            name="isExhibitNumberChecked"
                            label={t('addAncillary.validation.noSerialNumberPresent')}
                            control={control}
                            errors={errors}
                            onChange={handleExhibitNumberChange}
                          />
                        </Grid>
                      )}
                      {serialNumberEnabled && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            name="serialNumber"
                            blurred
                            label={`${t('addAncillary.list.section.serialNumber.label')}${
                              (isTransactionIllegal &&
                                !isExhibitNumberChecked &&
                                isAccessorySelected) ||
                              (isAccessorySelected &&
                                serialNumberEnabled &&
                                !isTransactionIllegal) ||
                              (isComponentSelected && !isExhibitNumberChecked)
                                ? '*'
                                : ''
                            } `}
                            setValue={setValue}
                            control={control}
                            errors={errors}
                            charsLimit={20}
                            disabled={isExhibitNumberChecked}
                          />
                        </Grid>
                      )}
                      {serialNumberEnabled && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            name="serialNumberConfirmation"
                            label={`${t(
                              'addAncillary.list.section.serialNumberConfirmation.label',
                            )}${
                              (isTransactionIllegal &&
                                !isExhibitNumberChecked &&
                                isAccessorySelected) ||
                              (isAccessorySelected &&
                                serialNumberEnabled &&
                                !isTransactionIllegal) ||
                              (isComponentSelected && !isExhibitNumberChecked)
                                ? '*'
                                : ''
                            } `}
                            setValue={setValue}
                            disableCopyPaste
                            control={control}
                            errors={errors}
                            charsLimit={20}
                            disabled={isExhibitNumberChecked}
                          />
                        </Grid>
                      )}
                      {(isAccessorySelected &&
                        !TRANSACTION_INITIAL_ILLEGAL.includes(
                          transactionTypeValue as ETransactionTypes,
                        )) ||
                        (transactionTypeValue ===
                          ETransactionTypes.DECLARED_PIECEMEAL_UNKNOWN_ORIGIN && (
                          <Grid size={{ xs: 12, lg: 3 }}>
                            <FormInput
                              name="assetId"
                              label={t('addAncillary.list.section.assetId.label')}
                              setValue={setValue}
                              control={control}
                              charsLimit={50}
                              errors={errors}
                            />
                          </Grid>
                        ))}
                      {isExhibitNumberEnabled && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            name="exhibitNumber"
                            label={`${t('addAncillary.list.section.exhibit.label')}${
                              (isTransactionIllegal &&
                                isExhibitNumberChecked &&
                                isAccessorySelected) ||
                              (isTransactionIllegal &&
                                isLotBatchEnabled &&
                                isLotBatchNumberNotPresent) ||
                              (isTransactionIllegal &&
                                isAccessorySelected &&
                                !serialNumberEnabled) ||
                              (isTransactionIllegal &&
                                isExhibitNumberChecked &&
                                isComponentSelected)
                                ? '*'
                                : ''
                            } `}
                            setValue={setValue}
                            control={control}
                            charsLimit={15}
                            errors={errors}
                          />
                        </Grid>
                      )}
                      {isImportMarking && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            key="importMarking"
                            name="importMarking"
                            label={t('addAncillary.list.section.importMarking.label')}
                            setValue={setValue}
                            control={control}
                            errors={errors}
                            charsLimit={100}
                          />
                        </Grid>
                      )}
                      {isAppliedMarking && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            key="appliedMarking"
                            name="appliedMarking"
                            label={t('addAncillary.list.section.appliedMarking.label')}
                            setValue={setValue}
                            control={control}
                            errors={errors}
                            charsLimit={100}
                          />
                        </Grid>
                      )}
                      {isManufacturedDateEnabled && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormDatePicker
                            name="manufacturedAt"
                            label={`${t('addAncillary.list.section.manufacturedDate.label')}${
                              TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD_REQUIRED.includes(
                                transactionTypeValue,
                              )
                                ? '*'
                                : ''
                            } `}
                            control={control}
                            disabled={ANCILLARY_TRANSACTIONS_WITH_AUTOCOMPLETED_MANUFACTURED_AT.includes(
                              transactionTypeValue,
                            )}
                            errors={errors}
                          />
                        </Grid>
                      )}
                      {isAmmunitionExpiryDateEnabled && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormDatePicker
                            name="ammunitionExpiryDate"
                            label={`${t(
                              'addAncillary.list.section.ammunitionExpiryDate.label',
                            )} `}
                            control={control}
                            errors={errors}
                          />
                        </Grid>
                      )}
                      {isAmmunitionHeadstampYearEnabled && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            name="headstampYear"
                            type={EFormInputTypes.number}
                            label={`${t('addAncillary.list.section.headstampYear.label')} `}
                            control={control}
                            errors={errors}
                          />
                        </Grid>
                      )}
                      {customFieldsAncillaries.map((customField) => (
                        <Grid size={{ xs: 12, lg: 3 }} key={customField.uniqueId}>
                          <FormCustomField
                            data={customField}
                            control={control}
                            setValue={setValue}
                            errors={errors}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid size={{ xs: 12 }} container columnSpacing={2}>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <FormRadioGroup
                          name="isCreateGroup"
                          label={t('addAncillary.details.section.toCreateAGroup.text')}
                          options={createGroupOptions}
                          control={control}
                          errors={errors}
                          disabled={hasPermissions([EPermission.MANAGE_GROUPS_LE]) !== true}
                        />
                      </Grid>
                      {isShowGroupNameFormElement && (
                        <Grid size={{ xs: 12, lg: 6 }}>
                          <FormInput
                            key="groupName"
                            name="groupName"
                            label={`${t('addAncillary.details.section.groupName.label')}* `}
                            setValue={setValue}
                            control={control}
                            charsLimit={255}
                            errors={errors}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </Accordion>
        </Grid>
        {isShowList && (
          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addAncillary.list.section.name')} alwaysExpanded>
              <Grid container sx={{ mb: 5 }}>
                <Grid
                  container
                  spacing={3}
                  size={{ xs: 12 }}
                  sx={{ flexWrap: { lg: 'nowrap' }, mb: 2 }}
                >
                  <StyledFirearmListBox size={{ xs: 12, lg: 6 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {t('addAncillary.list.section.downloadInfo.text')}
                    </Typography>

                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      sx={{
                        ml: 'auto',
                        justifyContent: 'flex-end',
                        display: 'flex',
                        alignItems: 'flex-end',
                      }}
                    >
                      <Button
                        onClick={handleGenerateAncillariesListTemplate}
                        label={t('addAncillary.list.section.downloadTemplate.button')}
                        icon={EIconTypes.download}
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        fullWidth={!isDesktop}
                        disabled={transactionTypeValue === ''}
                      />
                    </Grid>
                  </StyledFirearmListBox>

                  <StyledFirearmListBox size={{ xs: 12, lg: 6 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {t('addAncillary.list.section.importInfo.text')}
                    </Typography>

                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      sx={{
                        ml: 'auto',
                        justifyContent: 'flex-end',
                        display: 'flex',
                        alignItems: 'flex-end',
                      }}
                    >
                      <Button
                        onClick={() => {
                          setShowImportDialog(true);
                        }}
                        label={t('addAncillary.list.section.importAncillary.button')}
                        icon={EIconTypes.download}
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        fullWidth={!isDesktop}
                        disabled={transactionTypeValue === ''}
                      />
                    </Grid>
                  </StyledFirearmListBox>
                  <ImportAncillaries
                    open={showImportDialog}
                    handleClose={() => setShowImportDialog(false)}
                    changeAncillariesListValue={(ancillaryList) => {
                      remove();
                      setIsDataImportedFromFile(true);
                      ancillaryList.forEach((ancillaryListRow: any, index: number) => {
                        if (index !== 0) {
                          importItem(ancillaryListRow);
                        }
                      });
                    }}
                  />
                </Grid>
                {!!fields.length && (
                  <Grid size={{ xs: 12 }} rowSpacing={2} container>
                    <Typography
                      variant="subtitle1"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {`${t('addAncillary.list.section.totalRowsCount.text')}: `}
                      <Typography variant="body1" sx={{ ml: 1 }}>
                        {fields.length}
                      </Typography>
                    </Typography>
                    <Grid size={{ xs: 12 }}>
                      {fields.map((field, index) => (
                        <AddAncillaryListItem
                          key={field.id}
                          index={index}
                          arrayName="ancillaryList"
                          removeItem={() => remove(index)}
                          fields={fields}
                          control={control}
                          setValue={setValue}
                          errors={errors}
                          isAccessorySelected={isAccessorySelected}
                          serialNumberEnabled={serialNumberEnabled}
                          isLotBatchEnabled={isLotBatchEnabled}
                          transactionTypeValue={transactionTypeValue}
                          getValues={getValues}
                          customFields={customFieldsAncillaries}
                          isComponentSelected
                          isDataImportedFromFile={isDataImportedFromFile}
                          setIsDataImportedFromFile={setIsDataImportedFromFile}
                          isReloadCausedLocationSelect={isReloadCausedLocationSelect}
                          isManufacturedDateEnabled={isManufacturedDateEnabled}
                          isImportMarking={isImportMarking}
                          isAppliedMarking={isAppliedMarking}
                        />
                      ))}
                    </Grid>
                  </Grid>
                )}
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Button
                    onClick={() => {
                      append({
                        serialNumber: '',
                        serialNumberConfirmation: '',
                        assetId: '',
                        exhibitNumber: '',
                        manufacturedAt: '',
                        ...(isImportMarking ? { importMarking: '' } : {}),
                        ...(isAppliedMarking ? { appliedMarking: '' } : {}),
                      });
                    }}
                    fullWidth
                    label={t('addAncillary.list.addRow.button')}
                    icon={EIconTypes.plus}
                    size={EButtonSizes.small}
                  />
                </Grid>
              </Grid>
            </Accordion>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Accordion title={t('addAncillary.location.section.name')} alwaysExpanded>
            <Grid container columnSpacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, lg: 4 }}>
                <FormCheckbox
                  name=""
                  id="unassignedLocation"
                  checked={unassignedLocation}
                  label={t('addTransaction.location.section.unassignedLocation.label')}
                  control={control}
                  errors={errors}
                  onChange={(newState) => {
                    if (newState) setUnassignedLocationModalOpened(true);
                    else setValue('unassignedLocation', false);
                  }}
                />
              </Grid>
            </Grid>
            <SelectLocationTransactionPage
              show={!unassignedLocation}
              searchAddressesFor={id}
              canSelectOnlyExistingAddress={!canSelectFromMap}
              setValue={setValue}
              addressId={addressId}
            />
            {!unassignedLocation && (
              <Grid
                container
                size={{ xs: 12 }}
                columnSpacing={3}
                rowSpacing={2}
                sx={{ alignItems: 'flex-start' }}
              >
                {canSelectFromMap && (
                  <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                    <FormInput
                      name="address"
                      label={`${t('addAncillary.location.section.address.text')}* `}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                      placeholder={address}
                      InputProps={{ readOnly: true }}
                      disabled
                      sx={{ minWidth: '272px' }}
                      helperText={t('addAncillary.location.section.address.helperText')}
                    />
                  </Grid>
                )}
                {latitude && longitude && (
                  <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                    <StyledLocationMarkConfirmationMarginBottom>
                      <Typography variant="body1" sx={{ color: colors.success900 }}>
                        {t('addAncillary.location.section.locationMarked.text')}
                      </Typography>
                      <StyledIconWrapper>
                        <Icon type={EIconTypes.checkmark} color={colors.white} fontSize={14} />
                      </StyledIconWrapper>
                    </StyledLocationMarkConfirmationMarginBottom>
                  </Grid>
                )}
                {canSelectFromMap && (
                  <Grid size={{ xs: 12, md: 3, lg: 3 }}>
                    <Button
                      id="addTransaction_location_select-button"
                      onClick={() =>
                        navigate(
                          `${ERouteLinks.SelectLocation.replace(
                            ':id',
                            id,
                          )}?type=${type}&expand=ancillaries`,
                          {
                            state: {
                              ...routerState,
                              ...getValues(),
                              coords:
                                latitude && longitude ? [latitude, longitude] : undefined,
                              breadcrumbs: [
                                ...breadcrumbs.current,
                                t('selectLocationOnMap.page.text'),
                              ],
                              leId: id,
                              type,
                              canSelectOnlyExistingAddress: !canSelectFromMap,
                              returnLink: `${ERouteLinks.LegalEntityAddAncillary.replace(
                                ':id',
                                id,
                              )}?type=${type}&expand=ancillaries`,
                            },
                          },
                        )
                      }
                      fullWidth
                      label={t('selectLocationOnMap.page.text')}
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                      sx={{ marginTop: '28px' }}
                    />
                  </Grid>
                )}
                {isErrorLocation && (
                  <Grid size={{ xs: 12 }}>
                    <FormErrorLabel
                      label={
                        canSelectFromMap
                          ? t('addTransaction.location.section.selectLocationOnMap.errorLabel')
                          : t('addTransaction.location.section.selectLocation.errorLabel')
                      }
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </Accordion>
        </Grid>
        {isShowImporterDetails && (
          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addAncillary.importerDetails.section.name')} alwaysExpanded>
              <Grid container columnSpacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FormInput
                    name="importer"
                    label={t('addAncillary.importerDetails.section.nameOfImporter.label')}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(countryAll)}
                    name="countryOfImportUniqueId"
                    setValue={setValue}
                    label={`${t(
                      'addAncillary.importerDetails.section.countryOfImport.label',
                    )}* `}
                    control={control}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Accordion>
          </Grid>
        )}
        {isShowDocument && (
          <Grid size={{ xs: 12 }}>
            <Accordion title={`${t('addAncillary.document.section.name')} `} alwaysExpanded>
              <Box>
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                      <Alert
                        text={t('general.field.availableAttachmentFormats.text')}
                        variant={EAlertVariants.warning}
                      />
                    </Box>
                  </Grid>
                  {attachments?.fields?.map((attachment: any, index) => (
                    <Grid
                      container
                      size={{ xs: 12 }}
                      key={attachment.id}
                      sx={{ mb: !isDesktop ? 2 : 0 }}
                    >
                      <Grid size={{ xs: attachments.fields?.length > 0 ? 11 : 12, lg: 6 }}>
                        <FormInput
                          name={`attachments[${index}].attachmentName`}
                          label={t('addAncillary.document.section.attachmentName.label')}
                          control={control}
                          defaultValue={
                            attachment?.file?.name
                              ? removeFileExtension(attachment?.file?.name)
                              : ''
                          }
                          setValue={setValue}
                          errors={errors}
                          arrayName="attachments"
                          arrayIndex={index}
                          fieldName="attachmentName"
                          InputProps={{ disabled: attachment?.file === null }}
                        />
                      </Grid>
                      {!isDesktop && renderRemoveAttachmentButton(index)}
                      <Grid
                        size={{ xs: 12, lg: 3 }}
                        sx={{ marginTop: { lg: '28px' }, ml: { lg: 2 } }}
                      >
                        <Button
                          isUploadButton
                          onClick={(files) => handleUploadFile(files[0], index)}
                          fullWidth
                          label={t('addAncillary.document.section.browseFiles.button')}
                          id="add_file-button"
                          icon={EIconTypes.plus}
                          size={EButtonSizes.small}
                          isMultiple
                        />
                        {errors && errors.attachments && getAttachmentError(errors, index) && (
                          <FormErrorLabel
                            label={getAttachmentError(errors, index)?.message ?? ''}
                          />
                        )}
                      </Grid>
                      {isDesktop && renderRemoveAttachmentButton(index)}
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12, lg: 3 }} sx={{ mt: { xs: 2, lg: 0 } }}>
                    <Button
                      onClick={() =>
                        attachments.append({
                          attachmentName: '',
                          file: null,
                        })
                      }
                      fullWidth
                      label={t('addAncillary.document.section.addFile.button')}
                      id="add_file-button"
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Accordion>
          </Grid>
        )}
        <Grid container size={{ xs: 12, lg: 6 }} spacing={2} sx={{ mt: 4 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              onClick={handleCreateInitialTransaction}
              fullWidth
              label={t('general.save.button')}
              id="save-button"
              variant={EButtonVariants.contained}
              disabled={selectedAncillary === null || submitBlocked}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel-button"
              variant={EButtonVariants.outlined}
              onClick={() =>
                navigate(
                  `${ERouteLinks.ViewLegalEntity.replace(
                    ':id',
                    id,
                  )}?type=${type}&expand=ancillaries`,
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption">
              *{t('addAncillary.mandatoryField.label')}
            </Typography>
          </Grid>
        </Grid>
        <Grid sx={{ mt: 4 }}>
          {createInitialTransactionError && (
            <Box sx={{ mb: 2 }}>
              <Alert text={createInitialTransactionError} variant={EAlertVariants.error} />
            </Box>
          )}
        </Grid>
      </Grid>
      <FormModal
        open={unassignedLocationModalOpened}
        onClickClose={() => {
          setUnassignedLocationModalOpened(false);
          setValue('unassignedLocation', false);
        }}
        onClickApprove={() => {
          setValue('unassignedLocation', true);
          setUnassignedLocationModalOpened(false);
        }}
        title={t('transaction.unassigned.modal.title')}
        text={t('transaction.initial.unassigned.modal.text')}
        approveButtonLabel="general.confirm.label"
      />
    </div>
  );
};

export { LegalEntityAddAncillaryPage };
